import { z } from 'zod';
import { ArtifactTypeSchema } from '@terra/shared-schemas-ai';

export const PocEvidencePhaseSchema = z.enum([
  'BASELINE',
  'DETECTION',
  'EXPLOITATION',
  'VALIDATION',
]);

export const PocEvidenceTypeSchema = z.enum([
  'http',
  'artifact',
  'email',
  'websocket',
  'oob',
]);

export const POC_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'] as const;
export const POC_IMAGE_CAPTION_MAX_LENGTH = 500;
export const POC_IMAGE_MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const POC_MAX_IMAGE_BLOCKS = 5;

export const PocNarrativeBlockSchema = z.object({
  type: z.literal('narrative'),
  content: z
    .string()
    .describe('Markdown narrative that provides context for the evidence.'),
});

export const PocEvidenceReferenceSchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('HAR'),
    id: z.string(),
    redirectIds: z.array(z.string()).optional(),
  }),
  z.object({
    source: z.literal('INTERNAL_EVIDENCE'),
    id: z.string(),
  }),
]);

export const PocEvidenceBlockSchema = z.object({
  type: z.literal('evidence'),
  evidenceType: PocEvidenceTypeSchema.optional().describe(
    'Kind of evidence. "http" is backed by captured request/response traffic, "artifact" by a produced file/output, "email" by a sent/received message, "websocket" by frames of a WebSocket session, "oob" by an out-of-band Interactsh result.',
  ),
  artifactType: ArtifactTypeSchema.optional().describe(
    'For artifact evidence, the type of artifact produced.',
  ),
  phase: PocEvidencePhaseSchema.optional().describe(
    'Attack phase this evidence belongs to.',
  ),
  toolCallId: z
    .string()
    .optional()
    .describe(
      'ID of the tool call this evidence came from, when that call produced a browser session recording.',
    ),
  toolExecutionHarId: z
    .string()
    .optional()
    .describe(
      'For "http" evidence, the ID of the captured HTTP request/response (HAR). Fetch the full traffic via GET /tracker/tool-hars/{id}.',
    ),
  redirectHarIds: z
    .array(z.string())
    .optional()
    .describe(
      'For "http" evidence captured across a redirect chain, the ordered HAR ids of the hops following toolExecutionHarId (the anchor). Each is fetchable via GET /tracker/tool-hars/{id}.',
    ),
  evidenceRef: PocEvidenceReferenceSchema.optional().describe(
    'Typed reference to durable evidence fetched when the finding is rendered.',
  ),
  explanation: z
    .string()
    .describe('Human-readable explanation of what this evidence shows.'),
  whyItMatters: z
    .string()
    .optional()
    .describe('Why this evidence is significant to the finding.'),
  manualRequest: z
    .string()
    .optional()
    .describe(
      'Inlined HTTP request text for the evidence, when available without fetching the HAR.',
    ),
  manualResponse: z
    .string()
    .optional()
    .describe(
      'Inlined HTTP response text for the evidence, when available without fetching the HAR.',
    ),
});

export const PocImageBlockSchema = z.object({
  type: z.literal('image'),
  imageId: z.string(),
  mimeType: z.enum(POC_IMAGE_MIME_TYPES),
  fileName: z.string(),
  caption: z.string().max(POC_IMAGE_CAPTION_MAX_LENGTH).optional(),
});

export const PocBlockSchema = z.discriminatedUnion('type', [
  PocNarrativeBlockSchema,
  PocEvidenceBlockSchema,
  PocImageBlockSchema,
]);

export const PocBlocksSchema = z
  .array(PocBlockSchema)
  .superRefine((blocks, context) => {
    const imageBlocksCount = blocks.reduce(
      (count, block) => count + (block.type === 'image' ? 1 : 0),
      0,
    );

    if (imageBlocksCount > POC_MAX_IMAGE_BLOCKS) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `PoC supports up to ${POC_MAX_IMAGE_BLOCKS} image blocks`,
      });
    }
  })
  .describe(
    'Structured proof-of-concept for the finding: an ordered list of narrative, evidence, and image blocks. Evidence blocks (type: "evidence") are the proof/evidence backing the finding.',
  );

export type PocNarrativeBlock = z.infer<typeof PocNarrativeBlockSchema>;
export type PocEvidenceBlock = z.infer<typeof PocEvidenceBlockSchema>;
export type PocImageBlock = z.infer<typeof PocImageBlockSchema>;
export type PocEvidencePhase = z.infer<typeof PocEvidencePhaseSchema>;
export type PocEvidenceType = z.infer<typeof PocEvidenceTypeSchema>;
export type PocEvidenceReference = z.infer<typeof PocEvidenceReferenceSchema>;
export type PocBlock = z.infer<typeof PocBlockSchema>;
export type PocBlocks = z.infer<typeof PocBlocksSchema>;

export function parsePocBlocksTolerant(raw: unknown): PocBlock[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((candidate) => {
    const parsed = PocBlockSchema.safeParse(candidate);

    return parsed.success ? [parsed.data] : [];
  });
}
